import { afterNextRender, DestroyRef, Directive, ElementRef, inject, input, Renderer2 } from '@angular/core';

function toBooleanInput(v: boolean | string | undefined): boolean {
  return v === undefined || v === null ? true : typeof v === 'string' ? v !== 'false' : !!v;
}

@Directive({
  selector: '[appRipple]',
})
export class RippleDirective {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  readonly enabled = input(true, { alias: 'appRipple', transform: toBooleanInput });
  readonly rippleColor = input<string>();
  readonly rippleCentered = input<boolean>(false);
  readonly rippleDuration = input<number>(500);
  readonly rippleRadius = input<number>();

  private activeTimeouts = new Set<number>();

  constructor() {
    afterNextRender(() => {
      if (typeof window === 'undefined') return;
      this.prepareHost();
      this.bindEvents();
    });

    this.destroyRef.onDestroy(() => this.cleanup());
  }

  private prepareHost(): void {
    const host = this.el.nativeElement;
    const style = window.getComputedStyle(host);
    if (!style.position || style.position === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }
    this.renderer.setStyle(host, 'overflow', 'hidden');
  }

  private bindEvents() {
    const host = this.el.nativeElement;
    const pointerHandler = (event: PointerEvent) => {
      if (!this.enabled()) return;
      if (this.rippleCentered()) {
        this.createCenteredRipple();
        return;
      }
      this.createRipple(event.clientX, event.clientY);
    };

    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
        this.createCenteredRipple();
      }
    };
    host.addEventListener('pointerdown', pointerHandler);
    host.addEventListener('keydown', keyHandler);
    this.destroyRef.onDestroy(() => {
      host.removeEventListener('pointerdown', pointerHandler);
      host.removeEventListener('keydown', keyHandler);
    });

  }

  private createRipple(x: number, y: number) {

    const host = this.el.nativeElement;
    const rect = host.getBoundingClientRect();

    const rippleRadius = this.rippleRadius();
    const size = rippleRadius && rippleRadius > 0 ? rippleRadius : Math.max(rect.width, rect.height) * 2;

    const ripple = this.renderer.createElement('span');

    this.renderer.addClass(ripple, 'app-ripple');

    this.renderer.setStyle(ripple, 'width', `${size}px`);
    this.renderer.setStyle(ripple, 'height', `${size}px`);

    const duration = this.rippleDuration();

    this.renderer.setStyle(
      ripple,
      'transition',
      `transform ${duration}ms ease-out, opacity ${duration}ms linear`
    );

    const rippleColor = this.rippleColor();
    if (rippleColor) {
      this.renderer.setStyle(ripple, 'background', rippleColor);
    }

    const left = x - rect.left - size / 2;
    const top = y - rect.top - size / 2;

    this.renderer.setStyle(ripple, 'left', `${left}px`);
    this.renderer.setStyle(ripple, 'top', `${top}px`);

    this.renderer.appendChild(host, ripple);

    requestAnimationFrame(() => {
      this.renderer.addClass(ripple, 'app-ripple-active');
    });

    const timer = window.setTimeout(() => {
      try {
        this.renderer.removeChild(host, ripple);
      } catch { }
      this.activeTimeouts.delete(timer);
    }, duration + 100);

    this.activeTimeouts.add(timer);
  }

  private createCenteredRipple() {
    const rect = this.el.nativeElement.getBoundingClientRect();
    this.createRipple(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2
    );
  }

  private cleanup() {
    for (const timer of this.activeTimeouts) {
      clearTimeout(timer);
    }
    this.activeTimeouts.clear();
  }

}
